import React from 'react';
import {
  RiPlaneLine,
  RiTeamLine,
  RiBuildingLine,
  RiTimeLine,
  RiPriceTag3Line,
  RiArrowRightLine,
  RiFlightTakeoffLine,
  RiFlightLandLine,
  RiCalendarLine,
  RiDeleteBin6Line,
  RiCheckboxCircleFill,
  RiCloseCircleFill
} from '@remixicon/react';
import { Card, CardContent } from '@components/ui/Card';
import { TrustScore } from '@/features/trust-score';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatPrice } from '@/utils';
import { getAircraftModelName, getOperatorWithLocation } from '@/features/tails/utils';
import { apiClient } from '@/lib/api';
/**
 * ShortlistedCard Component
 * Displays a single shortlisted bid option with aircraft, operator, and flight details
 *
 * @param {Object} props
 * @param {Object} props.bid - Bid option data object
 * @param {Object} props.bidOption - Bid option data object
 * @param {Function} props.onViewBid - Callback when card is clicked
 * @param {boolean} props.isFirstInGroup - Whether this is the first card in a group
 */
const ShortlistedCard = ({ bid, bidOption, onViewBid }) => {
  const queryClient = useQueryClient();

  const handleClick = () => {
    if (onViewBid) {
      onViewBid(bid);
    }
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    try {
      await apiClient.updateBidOption(bidOption.bid_option_id, { shortlisted: false });
      queryClient.invalidateQueries({ queryKey: ['shortlistedBids'] });
      toast.success('Removed from shortlist', { position: 'bottom-right' });
    } catch {
      toast.error('Failed to remove from shortlist', { position: 'bottom-right' });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      day: 'numeric',
      month: 'short'
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const hours = date.getHours();
    const minutes = date.getMinutes();
    return `${hours}h ${minutes}m`;
  };


  return (
    <Card 
      className={`group hover:shadow-md transition-all !rounded-none duration-200 overflow-hidden bg-white border border-gray-200 cursor-pointer shadow-xl`}
      onClick={handleClick}
    >
      <CardContent className="p-4 space-y-3">
        {/* Aircraft Header */}
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 text-base">
                {getAircraftModelName(bidOption.aircraft)}
              </h3>
              <span className="border border-gray-200 px-2 !rounded-lg flex items-center gap-1 text-xs text-gray-600">
                {bidOption.availability_confirmed
                  ? <RiCheckboxCircleFill className="w-4 h-4 text-green-400" />
                  : <RiCloseCircleFill className="w-4 h-4 text-red-500" />
                }
                <span className="capitalize">{bidOption.availability_confirmed ? 'available' : 'unavailable'}</span>
              </span>
            </div>
            
            {/* Aircraft Details */}
            <div className="space-y-1 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <RiPlaneLine className="w-3.5 h-3.5" />
                <span>{bidOption.aircraft?.tail_number || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-1">
                <RiBuildingLine className="w-3.5 h-3.5" />
                <span>{getOperatorWithLocation(bidOption.operator)}</span>
              </div>
              <div className="flex items-center gap-1">
                <RiTeamLine className="w-3.5 h-3.5" />
                <span>
                  {bidOption.aircraft?.max_passengers || 0} passenger{bidOption.aircraft?.max_passengers !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>

          {/* Trust Score */}
          {bidOption.operator?.trust_score && (
            <div className="scale-75">
              <TrustScore score={bidOption.operator.rating_tiers} label="Trust" />
            </div>
          )}
        </div>

        {/* Flight Routes */}
        <div className="space-y-2 border-t border-gray-100 pt-2">
          <div className="flex items-center justify-between border-b border-gray-100 pb-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
              <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {bid.departure_airport}</span>
              <RiArrowRightLine className="w-4 h-4 text-gray-400" />
              <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{bid.arrival_airport}</span>
            </div>
            <div className="flex items-center gap-3 text-xs text-gray-500">
              <div className="flex items-center gap-1">
                <RiCalendarLine className="w-3.5 h-3.5" />
                <span>{formatDate(bid.departure_date)}</span>
              </div>
              <div className="flex items-center gap-1">
                <RiTimeLine className="w-3.5 h-3.5" />
                <span>{formatTime(bid.departure_date)}</span>
              </div>
            </div>
          </div>

          {bid.return_date && (
            <div className="flex items-center justify-between border-b border-gray-100 pb-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-900">
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightTakeoffLine className="w-4 h-4" /> {bid.arrival_airport}</span>
                <RiArrowRightLine className="w-4 h-4 text-gray-400" />
                <span className="bg-gray-200 flex items-center gap-1 rounded-full px-2"><RiFlightLandLine className="w-4 h-4" />{bid.departure_airport}</span>
              </div>
              <div className="flex items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <RiCalendarLine className="w-3.5 h-3.5" />
                  <span>{formatDate(bid.return_date)}</span>
                </div>
                <div className="flex items-center gap-1">
                  <RiTimeLine className="w-3.5 h-3.5" />
                  <span>{formatTime(bid.return_date)}</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Price & Delete */}
        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-1 px-2 py-1 bg-blue-50 rounded-lg">
            <RiPriceTag3Line className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-blue-900">{formatPrice(bidOption.final_price || 0)}</span>
          </div>
          <button
            onClick={handleDelete}
            className="p-0.5 rounded-md text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors border"
            aria-label="Remove from shortlist"
          >
            <RiDeleteBin6Line className="w-4 h-4" />
          </button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ShortlistedCard;
